import React, { useState, useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Avatar from '@atlaskit/avatar/Avatar';
import type { SizeType, AvatarClickEventHandler } from '@atlaskit/avatar/types';

import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile.ts';
import { fg } from '@confluence/feature-gating';

import type { Trigger, TriggerPosition } from './ProfileCardWrapper.tsx';
import { ProfileCardWrapper } from './ProfileCardWrapper.tsx';

type ProfileAvatarProps = {
	userId?: string | null;
	src?: string;
	onClick?: AvatarClickEventHandler;
	canViewProfile?: boolean;
	isAnonymous?: boolean;
	size?: SizeType;
	trigger?: Trigger;
	name?: string;
	stackIndex?: number;
	testId?: string;
	onVisibilityChange?: (isVisible: boolean) => void;
	position?: TriggerPosition;
	borderColor?: string;
	offset?: [number, number];
	isBlockLevel?: boolean;
};

const avatarWrapperStyles = {
	base: xcss({
		display: 'inline-flex',
		backgroundColor: 'color.background.neutral.subtle',
		borderWidth: '0',
		padding: 'space.0',
		borderRadius: 'radius.full',
		outlineOffset: 'space.0',
		':focus-within': {
			outlineStyle: 'solid',
			outlineWidth: 'border.width.focused',
			outlineColor: 'color.border.focused',
		},
	}),
	blockBase: xcss({
		display: 'flex',
	}),
	cursor: xcss({
		cursor: 'pointer',
	}),
};

const i18n = defineMessages({
	label: {
		id: 'profile.avatar.image',
		defaultMessage: 'profile image of a user',
		description: 'label for an avatar or profile image of a user',
	},
	ariaLabel: {
		id: 'profile.avatar.ariaLabel',
		defaultMessage: 'More information about {name}',
		description: 'Aria label attribute of the avatar button',
	},
});

export const ProfileAvatar = ({
	userId,
	canViewProfile = true,
	isAnonymous = false,
	size = 'medium' as SizeType,
	src,
	onClick,
	trigger,
	name,
	stackIndex,
	testId,
	position,
	borderColor,
	offset,
	isBlockLevel = false,
}: ProfileAvatarProps) => {
	const intl = useIntl();
	const avatarSrc = isAnonymous || !canViewProfile || !src ? undefined : src;
	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();

	const avatarRef = useRef<HTMLSpanElement | null>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [shouldReturnFocus, setShouldReturnFocus] = useState(false);
	const [wasKeyboardAction, setWasKeyboardAction] = useState(false);

	const handleVisibilityChange = (isVisible: boolean) => {
		setIsVisible(isVisible);
	};

	useEffect(() => {
		if (shouldReturnFocus && !isVisible && avatarRef.current) {
			avatarRef.current.focus();
			setShouldReturnFocus(false);
		}
	}, [isVisible, shouldReturnFocus]);

	const handleKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
		e.stopPropagation();
		if (e.key === 'Enter') {
			setWasKeyboardAction(true);
			setIsVisible((prev) => !prev);
			setShouldReturnFocus(true);
		} else if (e.key === 'Escape' && isVisible && wasKeyboardAction) {
			setIsVisible(false);
			setWasKeyboardAction(false);
			setShouldReturnFocus(true);
		}
	};

	const Icon = (
		<Avatar
			src={avatarSrc}
			size={size}
			target="_blank"
			name={fg('a11y_whiteboards_changes') ? '' : name || ''}
			{...(fg('a11y_whiteboards_changes') ? {} : { label: intl.formatMessage(i18n.label) })}
			stackIndex={stackIndex}
			testId={testId}
			borderColor={borderColor}
			onClick={onClick}
		/>
	);

	// Compute ARIA attributes based on feature flag
	const getAriaProps = () => {
		if (fg('a11y_whiteboards_changes')) {
			// When feature flag is enabled, don't add ARIA attributes to the div
			return {};
		}

		// Legacy behavior: always add ARIA attributes when feature flag is off
		return {
			'aria-haspopup': 'true' as const,
			'aria-expanded': isVisible,
			'aria-label': intl.formatMessage(i18n.ariaLabel, { name: name || '' }),
			role: 'button' as const,
			tabIndex: 0,
		};
	};

	return (
		<ProfileCardWrapper
			userId={userId}
			canViewProfile={canViewProfile}
			isAnonymous={isAnonymous}
			trigger={trigger}
			fullName={name || ''}
			disabledAriaAttributes={fg('a11y_whiteboards_changes')}
			onVisibilityChange={handleVisibilityChange}
			position={position}
			offset={offset}
		>
			<Box
				xcss={[
					avatarWrapperStyles.base,
					isBlockLevel && avatarWrapperStyles.blockBase,
					canViewProfile && hasViewUserProfilePermission && avatarWrapperStyles.cursor,
				]}
				testId="avatarWrapperTestId"
				ref={avatarRef}
				onKeyDown={handleKeyDown}
				{...getAriaProps()}
			>
				{Icon}
			</Box>
		</ProfileCardWrapper>
	);
};
